/** Sprints over time, by distance and load (DESIGN §40). */
import { norm } from "./plan.js";
import { runLoad, runType } from "./running.js";
import { isDistance, isInParts } from "./sets.js";
import type { Block, Session } from "./types.js";

/** Slower than this over the distance, a "time" is a speed typed in the wrong field. */
const SLOWEST_SPRINT = 4;

export type SprintSeries = "free" | "resisted" | `kg:${number}`;

export interface SprintRep {
  date: string;
  name: string;
  meters: number;
  kg: number | null;
  resisted: boolean;
  series: SprintSeries;
  time: number | null;
  /** What was logged as a time but can't be one. */
  suspect: number | null;
  speed: number | null;
}

type Workout = { date: string; session?: Pick<Session, "blocks" | "logs"> | null };

function exercises(blocks: Block[] | null | undefined) {
  const out = new Map<string, NonNullable<Block["exercises"]>[number]>();
  for (const b of blocks ?? []) for (const e of b.exercises ?? []) if (e.id && !out.has(norm(e.id))) out.set(norm(e.id), e);
  return out;
}

export function sprintReps(workouts: Workout[]): SprintRep[] {
  const out: SprintRep[] = [];
  for (const w of workouts) {
    const byID = exercises(w.session?.blocks);
    for (const log of w.session?.logs ?? []) {
      if (!isDistance(log) || !(log.amount != null && log.amount > 0)) continue;
      const exercise = byID.get(norm(log.exerciseID));
      if (isInParts(log, exercise)) continue;
      const name = (exercise?.name ?? log.exerciseKey ?? "Run").trim();
      const type = runType(name);
      const resisted = exercise?.isResisted === true || type === "resisted";
      if (!resisted && type !== "sprint" && type !== "accel") continue;
      const logged = log.time != null && log.time > 0 ? log.time : null;
      const suspect = logged != null && log.amount / logged < SLOWEST_SPRINT ? logged : null;
      const time = suspect == null ? logged : null;
      const speed = log.topSpeed != null && log.topSpeed > 0 ? log.topSpeed : null;
      if (logged == null && speed == null) continue;
      const kg = runLoad(log);
      const series: SprintSeries = kg != null ? `kg:${kg}` : resisted ? "resisted" : "free";
      out.push({ date: w.date, name, meters: log.amount, kg, resisted, series, time, suspect, speed });
    }
  }
  // Whoever typed one speed as a time typed that run's others the same way that day.
  const typedSpeeds = new Set(out.filter((r) => r.suspect != null).map((r) => r.date + "|" + r.name.toLowerCase()));
  for (const r of out) {
    if (r.time != null && typedSpeeds.has(r.date + "|" + r.name.toLowerCase())) {
      r.suspect = r.time;
      r.time = null;
    }
  }
  return out.filter((r) => r.time != null || r.speed != null || r.suspect != null);
}

export type SprintMeasure = "speed" | "time";

export interface SprintPoint {
  date: string;
  value: number;
  rep: SprintRep;
}

export interface SprintChart {
  meters: number;
  measure: SprintMeasure;
  series: { key: SprintSeries; kg: number | null; resisted: boolean; points: SprintPoint[] }[];
}

const better = (measure: SprintMeasure, a: number, b: number) => (measure === "speed" ? a > b : a < b);
const valueOf = (rep: SprintRep, measure: SprintMeasure) => (measure === "speed" ? rep.speed : rep.time);

export function seriesOrder(a: SprintSeries, b: SprintSeries): number {
  const rank = (s: SprintSeries) => (s === "free" ? [2, 0] : s === "resisted" ? [1, 0] : [0, Number(s.slice(3))]);
  const [x, y] = [rank(a), rank(b)];
  return x[0]! - y[0]! || x[1]! - y[1]!;
}

/** One chart a distance; a point a day and load, that day's best in the chart's measure. */
export function sprintCharts(reps: SprintRep[]): SprintChart[] {
  const byMeters = new Map<number, SprintRep[]>();
  for (const r of reps) byMeters.set(r.meters, [...(byMeters.get(r.meters) ?? []), r]);
  return [...byMeters.entries()]
    .sort(([a], [b]) => a - b)
    .map(([meters, at]): SprintChart => {
      const measure: SprintMeasure = at.some((r) => r.speed != null) ? "speed" : "time";
      const series = new Map<SprintSeries, Map<string, SprintPoint>>();
      for (const rep of at) {
        const value = valueOf(rep, measure);
        if (value == null) continue;
        const days = series.get(rep.series) ?? new Map<string, SprintPoint>();
        series.set(rep.series, days);
        const best = days.get(rep.date);
        if (!best || better(measure, value, best.value)) days.set(rep.date, { date: rep.date, value, rep });
      }
      return {
        meters,
        measure,
        series: [...series.entries()]
          .sort(([a], [b]) => seriesOrder(a, b))
          .map(([key, days]) => {
            const points = [...days.values()].sort((a, b) => a.date.localeCompare(b.date));
            return { key, kg: points[0]!.rep.kg, resisted: points[0]!.rep.resisted, points };
          }),
      };
    })
    .filter((c) => c.series.length > 0);
}

export interface SprintRow {
  date: string;
  name: string;
  meters: number;
  kg: number | null;
  reps: SprintRep[];
  /** Index of the row's best rep: top speed when any rep has one, else time; -1 for none. */
  best: number;
}

/** Every rep, a row a day, run, distance and load, newest first. */
export function sprintRows(reps: SprintRep[]): SprintRow[] {
  const rows = new Map<string, SprintRow>();
  for (const r of reps) {
    const key = [r.date, r.name.toLowerCase(), r.meters, r.kg ?? ""].join("|");
    const row = rows.get(key) ?? { date: r.date, name: r.name, meters: r.meters, kg: r.kg, reps: [], best: -1 };
    row.reps.push(r);
    rows.set(key, row);
  }
  for (const row of rows.values()) {
    const measure: SprintMeasure = row.reps.some((r) => r.speed != null) ? "speed" : "time";
    let best: number | null = null;
    row.reps.forEach((r, i) => {
      const v = valueOf(r, measure);
      if (v != null && (best == null || better(measure, v, best))) {
        best = v;
        row.best = i;
      }
    });
  }
  return [...rows.values()].sort((a, b) => b.date.localeCompare(a.date) || a.meters - b.meters || a.name.localeCompare(b.name) || (a.kg ?? -1) - (b.kg ?? -1));
}
